import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState, DateRangePicker } from "../components/UI";
import { ClipboardList, Download } from "lucide-react";
import { toast } from "sonner";

export default function AuditLog() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [action, setAction] = useState("");
  const [downloading, setDownloading] = useState(false);

  const canExport = ["hr", "company_admin", "super_admin"].includes(user?.role);

  const load = async () => {
    const params = {};
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    if (action) params.action = action;
    const { data } = await api.get("/audit-logs", { params });
    setLogs(data);
  };
  useEffect(() => { load().catch(() => {}); }, [dateRange.from, dateRange.to, action]); // eslint-disable-line

  const downloadDisclaimerCsv = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/disclaimer/acks/export.csv", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `prophr-disclaimer-acks-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Disclaimer acknowledgements CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <SectionHeader
        title="Audit Log"
        subtitle="Every action is logged for compliance. Retention per your tenant policy."
        right={canExport ? (
          <button
            className="btn-ghost text-xs"
            onClick={downloadDisclaimerCsv}
            disabled={downloading}
            data-testid="audit-export-disclaimer-csv"
            title="Compliance-grade export of every disclaimer acknowledgement"
          >
            <Download size={12} strokeWidth={2}/>
            {downloading ? "Preparing…" : "Disclaimer acks CSV"}
          </button>
        ) : null}
      />

      <div className="data-cell p-4 mb-4 flex flex-wrap gap-3 items-center">
        <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Filter by action…" className="input !w-64" data-testid="audit-action-filter"/>
        <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} testid="audit-date-range"/>
        <span className="text-xs font-mono text-slate-400 ml-auto">{logs.length} entries</span>
      </div>

      {logs.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No entries yet" subtitle="Activity will appear here as your team uses PropHR." />
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto rounded">
          <table className="w-full text-xs font-mono">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow">Timestamp</th>
                <th className="px-4 py-3 eyebrow">Actor</th>
                <th className="px-4 py-3 eyebrow">Action</th>
                <th className="px-4 py-3 eyebrow">Resource</th>
                <th className="px-4 py-3 eyebrow">Metadata</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 row-hover">
                  <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-slate-800">{l.actor_email || "system"}</td>
                  <td className="px-4 py-2"><span className="pill pill-medium">{l.action}</span></td>
                  <td className="px-4 py-2 text-slate-600">{l.resource_type}:{l.resource_id?.slice(0, 8)}</td>
                  <td className="px-4 py-2 text-slate-500 max-w-xs truncate">{Object.keys(l.metadata || {}).length > 0 ? JSON.stringify(l.metadata) : ","}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
